import { useEffect, useState } from 'react'
import { phaserEvents } from '../../../events/EventCenter'

export interface ChatPanelUnread {
  /** Chats with anything unread — not a message total. */
  chats: number
  /** How many of those chats hold a mention. */
  mentionChats: number
}

const ZERO: ChatPanelUnread = { chats: 0, mentionChats: 0 }
const normalize = (summary?: Partial<ChatPanelUnread> | null): ChatPanelUnread => ({
  chats: summary?.chats ?? 0,
  mentionChats: summary?.mentionChats ?? 0,
})
// Module-level so a consumer mounting AFTER the panel's last emit (the panel only emits on
// change) still starts from the current count instead of zero.
let latest: ChatPanelUnread = ZERO
// The chat panel emits zero when it unmounts (sign-out), which clears this for the next account.
const onLatest = (summary: ChatPanelUnread) => {
  latest = normalize(summary)
}
phaserEvents.on('sevv-chat-unread', onLatest)
import.meta.hot?.dispose(() => phaserEvents.off('sevv-chat-unread', onLatest))

/**
 * Mirrors the chat panel's account-wide unread-chat count, which it emits on
 * `sevv-chat-unread`. Stays at zero when the panel isn't mounted.
 */
export function useChatPanelUnread(): ChatPanelUnread {
  const [totals, setTotals] = useState<ChatPanelUnread>(() => latest)

  useEffect(() => {
    const onUnread = (summary: ChatPanelUnread) => {
      setTotals(normalize(summary))
    }
    phaserEvents.on('sevv-chat-unread', onUnread)
    // An emit between render and this subscribe would otherwise be missed.
    setTotals(latest)
    return () => {
      phaserEvents.off('sevv-chat-unread', onUnread)
    }
  }, [])

  return totals
}
